import { HttpClient } from '@angular/common/http';
import { Injectable, computed, inject, signal } from '@angular/core';
import { Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import { ConfigService } from '@neworg-net/amsuite-core/config';
import { requireHostUrl } from '../shared/required-host-url';
import { httpOptions } from '../constants/http-options.constant';
import { WorkspaceCapabilitiesDto, WorkspaceMeDto } from '../models';

/**
 * Stato condiviso dell'utente Workspace (GET /v1/me).
 *
 * Espone le capabilities come signal: i componenti dashboard/template fanno
 * gating su `canManageTenant` / `canManageSystem` / `canCreatePersonal`.
 * Bearer + X-Party-Owner-Id sono iniettati centralmente dagli interceptor per
 * ogni URL Workspace registrato (PartyOwnerInterceptor attende
 * getDataOwner se serve), quindi nessun header va impostato qui.
 */
@Injectable({ providedIn: 'root' })
export class WorkspaceMeService {
  private _http = inject(HttpClient);
  private readonly config = inject(ConfigService);

  private _meUrl = requireHostUrl(this.config, 'workspace') + '/v1/me';

  private _me = signal<WorkspaceMeDto | null>(null);
  /** Snapshot completo dell'ultima risposta /v1/me (null finche non caricato). */
  readonly me = this._me.asReadonly();

  /** Capabilities correnti, o null se /v1/me non e ancora stato caricato. */
  readonly capabilities = computed<WorkspaceCapabilitiesDto | null>(
    () => this._me()?.capabilities ?? null
  );

  readonly canCreatePersonal = computed(() => !!this.capabilities()?.canCreatePersonalDashboard);
  readonly canManageTenant = computed(() => !!this.capabilities()?.canManageTenantTemplates);
  readonly canManageSystem = computed(() => !!this.capabilities()?.canManageSystemTemplates);
  /** True se l'utente puo gestire almeno un tipo di template (Tenant o System). */
  readonly canManageAny = computed(() => this.canManageTenant() || this.canManageSystem());

  /** Inserted by Angular inject() migration for backwards compatibility */
  constructor(...args: unknown[]);
  constructor() {}

  /** Chiamata grezza a GET /v1/me senza side-effect sullo stato. */
  getMe(): Observable<WorkspaceMeDto> {
    return this._http.get<WorkspaceMeDto>(this._meUrl, httpOptions);
  }

  /**
   * Carica /v1/me e popola i signal condivisi. Idempotente: ogni chiamata
   * aggiorna lo stato. Chiamare una volta all'avvio (es. DashboardComponent,
   * rotta di landing) dopo che il data owner e risolvibile.
   */
  load(): Observable<WorkspaceMeDto> {
    return this.getMe().pipe(tap(dto => this._me.set(dto)));
  }
}
